import { useMutation, useQueryClient } from '@tanstack/react-query';
import { meApi, spaceApi, ApiError } from '../services/api';
import type { Space } from '../types/spaces';

/** Create a new Shared Space. The caller becomes its Owner. */
export function useCreateSpace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string }) => meApi.createSpace(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });
    },
  });
}

/** Rename / edit description / archive (`{ archived: true }`). */
export function useUpdateSpace(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { name?: string; description?: string; archived?: boolean }) => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).space.update(patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'detail'] });
      }
    },
  });
}

/** Restore an archived space. Admin or Owner. */
export function useUnarchiveSpace(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!spaceId) throw new Error('spaceId required');
      return spaceApi(spaceId).space.unarchive();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'detail'] });
      }
    },
  });
}

/**
 * Transfer ownership. Owner-only. Backend may return 409 on a transaction
 * race — we retry once before surfacing the error.
 */
export function useTransferOwnership(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string): Promise<{
      space: Space;
      previousOwnerUserId: string;
      newOwnerUserId: string;
    }> => {
      if (!spaceId) throw new Error('spaceId required');
      const api = spaceApi(spaceId).space;
      try {
        return await api.transferOwnership(userId);
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          return await api.transferOwnership(userId);
        }
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'members'] });
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'detail'] });
      }
    },
  });
}
